/**
 * Sync state shared between the header badge and the sync view.
 *
 * The redirect flow means sign-in destroys the page. Anything that should
 * happen "after signing in" cannot be a continuation in memory — it has to be
 * a note left in sessionStorage and picked up on the next load. That is what
 * RESUME_KEY is for.
 */

import { create } from 'zustand';
import type { AccountInfo } from '@azure/msal-browser';
import * as msal from '../persistence/onedrive/msal.ts';
import { syncNow, type SyncOutcome } from '../persistence/onedrive/sync.ts';
import * as repo from '../persistence/repo.ts';

let autoSyncTimer: ReturnType<typeof setTimeout> | null = null;
let initialization: Promise<void> | null = null;

function scheduleAutoSync() {
  if (autoSyncTimer !== null) clearTimeout(autoSyncTimer);
  autoSyncTimer = setTimeout(() => {
    const { account, busy } = useSyncStore.getState();
    if (account !== null && !busy) {
      void useSyncStore.getState().sync();
    }
  }, 600);
}

/** Set immediately before navigating to Microsoft; consumed on return. */
const RESUME_KEY = 'expenses.sync.resume';

interface SyncState {
  configured: boolean;
  account: AccountInfo | null;
  /** True while navigating away — the UI should stop offering the button. */
  redirecting: boolean;
  busy: boolean;
  lastSyncedAt: string | null;
  dirty: boolean;
  lastOutcome: SyncOutcome | null;

  init: () => Promise<void>;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  /** Re-authenticate an expired session without signing out first. */
  reauthenticate: () => Promise<void>;
  sync: () => Promise<void>;
}

export const useSyncStore = create<SyncState>((set, get) => ({
  configured: msal.isSyncConfigured(),
  account: null,
  redirecting: false,
  busy: false,
  lastSyncedAt: null,
  dirty: false,
  lastOutcome: null,

  /**
   * Runs once on load. Consumes any pending redirect response, then resumes
   * the sync that the sign-in was started for.
   */
  async init() {
    if (initialization) return initialization;
    initialization = (async () => {
      const meta = await repo.getSyncMeta();
      set({ lastSyncedAt: meta.lastSyncedAt, dirty: meta.dirty });

      // Listen for local mutations to update dirty badge and trigger auto-sync
      repo.onDirtyChange(() => {
        void repo.getSyncMeta().then((latestMeta) => {
          set({ dirty: latestMeta.dirty });
          if (get().account !== null && !get().busy) {
            scheduleAutoSync();
          }
        });
      });

      if (!msal.isSyncConfigured()) return;

      let returnedFromRedirect = false;
      try {
        const { redirectResult } = await msal.initializeAuth();
        returnedFromRedirect = redirectResult !== null;
        set({ account: await msal.getAccount() });
      } catch {
        set({ account: null });
        return;
      }

      const wanted = sessionStorage.getItem(RESUME_KEY) !== null;
      sessionStorage.removeItem(RESUME_KEY);

      if ((returnedFromRedirect || wanted) && get().account !== null) {
        await get().sync();
      }
    })();
    return initialization;
  },

  async signIn() {
    set({ redirecting: true });
    // Left before navigating, read after coming back.
    sessionStorage.setItem(RESUME_KEY, 'sync');
    try {
      await msal.signIn();
    } catch (error) {
      sessionStorage.removeItem(RESUME_KEY);
      set({
        redirecting: false,
        lastOutcome: {
          status: 'error',
          pulled: 0,
          pushed: 0,
          updated: 0,
          configBothChanged: false,
          detail: error instanceof Error ? error.message : String(error),
        },
      });
    }
  },

  async signOut() {
    set({ redirecting: true });
    try {
      await msal.signOut();
    } catch {
      set({ redirecting: false });
    }
  },

  async reauthenticate() {
    set({ redirecting: true });
    try {
      await msal.reauthenticate();
    } catch (error) {
      // The redirect replaces the page, so reaching here means it never
      // started — say so instead of leaving the button spinning.
      set({
        redirecting: false,
        lastOutcome: {
          status: 'error',
          pulled: 0,
          pushed: 0,
          updated: 0,
          configBothChanged: false,
          detail: error instanceof Error ? error.message : String(error),
        },
      });
    }
  },

  async sync() {
    if (get().busy) return;
    set({ busy: true });
    try {
      const outcome = await syncNow();
      const meta = await repo.getSyncMeta();
      set({ lastOutcome: outcome, lastSyncedAt: meta.lastSyncedAt, dirty: meta.dirty });
    } catch (error) {
      set({ lastOutcome: {
        status: 'error', pulled: 0, pushed: 0, updated: 0, configBothChanged: false,
        detail: error instanceof Error ? error.message : String(error),
      } });
    } finally {
      set({ busy: false });
      if (get().dirty && get().lastOutcome?.status === 'ok') scheduleAutoSync();
    }
  },
}));
